import React, { useState } from 'react';
import { Card, List, Label, Header, Accordion, Icon, Loader } from 'semantic-ui-react';
import { TopTeamsDetailDto, TeamSalespersonsDto, TopPerformersDetailDto } from '../../services/dashboard/dashboardService';
import dashboardService from '../../services/dashboard/dashboardService';
import GlassmorphismTheme from '../../styles/glassmorphism';

interface TopTeamsListProps {
  teams: TopTeamsDetailDto[];
  title: string;
  isBest?: boolean;
  days?: number;
}

const TopTeamsList: React.FC<TopTeamsListProps> = ({ teams, title, isBest = true, days }) => {
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const [loadingTeams, setLoadingTeams] = useState<Set<string>>(new Set());
  const [teamSalespersons, setTeamSalespersons] = useState<Map<string, TeamSalespersonsDto>>(new Map());

  if (teams.length === 0) {
    return (
      <Card style={{ ...GlassmorphismTheme.components.glassCard, width: '100%' }}>
        <Card.Content>
          <Header as="h3" style={{ color: GlassmorphismTheme.colors.solid.textLight }}>
            {title}
          </Header>
          <p style={{ color: GlassmorphismTheme.colors.solid.textLight, textAlign: 'center', padding: '20px' }}>
            Nenhum dado disponível
          </p>
        </Card.Content>
      </Card>
    );
  }

  const handleTeamClick = async (teamId: string) => {
    if (expandedTeams.has(teamId)) {
      // Fechar
      const newExpanded = new Set(expandedTeams);
      newExpanded.delete(teamId);
      setExpandedTeams(newExpanded);
    } else {
      // Abrir e carregar vendedores
      setExpandedTeams(new Set(expandedTeams).add(teamId));
      
      // Se já carregou antes, não precisa carregar novamente
      if (teamSalespersons.has(teamId)) {
        return;
      }

      setLoadingTeams(new Set(loadingTeams).add(teamId));
      try {
        // Inverter isBest: quando isBest=true (melhor performance), queremos mostrar MELHORES vendedores
        // quando isBest=false (pior performance), queremos mostrar PIORES vendedores
        const salespersons = await dashboardService.getTeamSalespersons(teamId, days, isBest);
        setTeamSalespersons(new Map(teamSalespersons).set(teamId, salespersons));
      } catch (error) {
        console.error('Erro ao carregar vendedores do time:', error);
      } finally {
        const newLoading = new Set(loadingTeams);
        newLoading.delete(teamId);
        setLoadingTeams(newLoading);
      }
    }
  };

  return (
    <Card style={{ ...GlassmorphismTheme.components.glassCard, width: '100%' }}>
      <Card.Content>
        <Header as="h3" style={{ color: GlassmorphismTheme.colors.solid.textLight, marginBottom: '15px' }}>
          {title}
        </Header>
        <List divided relaxed>
          {teams.map((team, index) => {
            const isExpanded = expandedTeams.has(team.teamId);
            const isLoading = loadingTeams.has(team.teamId);
            const salespersons = teamSalespersons.get(team.teamId);

            return (
              <React.Fragment key={team.teamId}>
                <List.Item 
                  style={{ 
                    padding: '10px 0',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleTeamClick(team.teamId)}
                >
                  <List.Content>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <Label 
                        circular 
                        size="small" 
                        color={isBest ? 'green' : 'red'}
                        style={{ marginRight: '10px', minWidth: '24px', textAlign: 'center' }}
                      >
                        {index + 1}
                      </Label>
                      <div style={{ flex: 1 }}>
                        <strong style={{ color: GlassmorphismTheme.colors.solid.textLight, fontSize: '1em' }}>
                          {team.teamName}
                        </strong>
                        <div style={{ fontSize: '0.85em', color: GlassmorphismTheme.colors.solid.textLight, marginTop: '4px' }}>
                          Gerente: {team.managerName} | Vendedores: {team.salespersonsCount}
                        </div>
                      </div>
                      <Icon 
                        name={isExpanded ? 'chevron down' : 'chevron right'} 
                        style={{ color: GlassmorphismTheme.colors.solid.textLight }}
                      />
                    </div>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(2, 1fr)', 
                      gap: '8px',
                      fontSize: '0.85em',
                      color: GlassmorphismTheme.colors.solid.textLight,
                      marginTop: '8px'
                    }}>
                      <div>
                        <strong>Criados:</strong> {team.contratosCriados}
                      </div>
                      <div>
                        <strong>Assinados:</strong> {team.contratosAssinados}
                      </div>
                      <div>
                        <strong>Aprovados:</strong> {team.contratosAprovados}
                      </div>
                      <div>
                        <strong>Taxa Conversão:</strong>{' '}
                        <Label 
                          size="tiny" 
                          color={isBest ? 'green' : 'orange'}
                          style={{ marginLeft: '5px' }}
                        >
                          {team.taxaConversao.toFixed(1)}%
                        </Label>
                      </div>
                    </div>
                  </List.Content>
                </List.Item>
                
                {/* Dropdown com vendedores */}
                {isExpanded && (
                  <List.Item style={{ padding: '10px 0 10px 30px', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}>
                    <List.Content>
                      {isLoading ? (
                        <Loader active inline="centered" size="small" />
                      ) : salespersons && salespersons.salespersons.length > 0 ? (
                        <div>
                          <Header as="h5" style={{ color: GlassmorphismTheme.colors.solid.textLight, marginBottom: '10px' }}>
                            Vendedores do Time
                          </Header>
                          <List divided relaxed>
                            {salespersons.salespersons.map((salesperson) => (
                              <List.Item key={salesperson.userId} style={{ padding: '8px 0' }}>
                                <List.Content>
                                  <div style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'repeat(2, 1fr)', 
                                    gap: '8px',
                                    fontSize: '0.8em',
                                    color: GlassmorphismTheme.colors.solid.textLight
                                  }}>
                                    <div>
                                      <strong>{salesperson.userName}</strong>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                      <Label size="tiny" color="blue">
                                        {salesperson.taxaConversao.toFixed(1)}%
                                      </Label>
                                    </div>
                                    <div>
                                      Criados: {salesperson.contratosCriados}
                                    </div>
                                    <div>
                                      Assinados: {salesperson.contratosAssinados}
                                    </div>
                                    <div>
                                      Aprovados: {salesperson.contratosAprovados}
                                    </div>
                                  </div>
                                </List.Content>
                              </List.Item>
                            ))}
                          </List>
                        </div>
                      ) : (
                        <p style={{ color: GlassmorphismTheme.colors.solid.textLight, textAlign: 'center', padding: '10px' }}>
                          Nenhum vendedor encontrado
                        </p>
                      )}
                    </List.Content>
                  </List.Item>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Card.Content>
    </Card>
  );
};

export default TopTeamsList;

